import { makeStyles } from '@fluentui/react-components';
import { PeopleTeam20Regular } from '@fluentui/react-icons';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IFilterItem } from '../../../data-types/filterItem';
import { FilterTabDefinition } from '../../../data-types/filterTabDefinition';
import { useFilterPickerState } from '../../../hooks/useFilterPickerState';
import { customPalette } from '../../../theme';
import { getFilterMenuAriaLabel, getUpdatedSelectedFilters, reorderCheckedItems } from '../../../utils/FilterUtils';
import FilterPickerPopover from '../filter-picker/FilterPickerPopover';

export interface ITabFilterButtonProps {
    label?: string;
    tabs: FilterTabDefinition[];
    filterName: string;
    searchValue: string;
    setSearchValue: (value: string) => void;
    onButtonClick?: (filterName: string) => void;
    disabled?: boolean;
}

const useStyles = makeStyles({
    groupIcon: {
        fontSize: '13px',
        marginRight: '10px',
        width: '13px',
        color: customPalette.themePrimary,
        flexShrink: 0,
    },
});

const TabFilterButton: React.FC<ITabFilterButtonProps> = ({
    label,
    tabs,
    filterName,
    searchValue,
    disabled,
    setSearchValue,
    onButtonClick,
}) => {
    const { t } = useTranslation();
    const styles = useStyles();
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const activeTab = tabs[activeTabIndex];
    const isItemSelected = activeTab?.checkedItems.length > 0;
    const isMultiSelect = !activeTab?.isSingleSelect;

    const clearOtherTabsAndSelectItems = (selectedItems: IFilterItem[]) => {
        tabs.forEach((tab, index) => {
            if (index !== activeTabIndex) {
                tab.setCheckedItems([]);
            }
        });
        tabs[activeTabIndex].setCheckedItems(selectedItems);
    };

    const handleCommit = (filteredItems: IFilterItem[], committedFilterName: string) => {
        const newSelectedItems: IFilterItem[] = getUpdatedSelectedFilters(filteredItems, activeTab.checkedItems, activeTab.isSingleSelect);
        activeTab.setCheckedItems(newSelectedItems);
        clearOtherTabsAndSelectItems(newSelectedItems);

        if (onButtonClick) {
            onButtonClick(committedFilterName);
        }
    };

    const tabLabels = useMemo(() => {
        return tabs.map((tab) => tab.label);
    }, [tabs]);

    const orderedItems = useMemo(() => {
        return reorderCheckedItems(activeTab?.checkedItems, activeTab?.items, activeTab.label);
    }, [activeTab.items, activeTab.checkedItems, activeTab.label]);

    const groupIcon = activeTab?.isSingleSelect ? <PeopleTeam20Regular className={styles.groupIcon} /> : null;

    const picker = useFilterPickerState({
        items: orderedItems,
        committedSelection: activeTab?.checkedItems ?? [],
        filterName,
        isFiltered: isItemSelected,
        noResultsText: activeTab?.noElementsFoundtext,
        searchItemName: 'item',
        searchValue,
        setSearchValue,
        onCommit: handleCommit,
    });

    const handleSingleSelectItem = (item: IFilterItem) => {
        handleCommit([item], filterName);
    };

    const buttonId: string = `${filterName}-button`;

    return (
        <FilterPickerPopover
            buttonId={buttonId}
            label={t(label || filterName)}
            ariaLabel={t(label || filterName) ?? ''}
            isActive={isItemSelected}
            isDisabled={disabled}
            filterMenuAriaLabel={getFilterMenuAriaLabel(label || filterName, t)}
            searchBoxPlaceholderText={t('search')}
            clearAllText={t('clearAll')}
            isMultiSelect={isMultiSelect}
            draftSelectedIds={picker.draftSelectedIds}
            visibleItems={picker.visibleItems}
            hasResults={picker.hasResults}
            noResultsText={activeTab?.noElementsFoundtext}
            announcementMessage={picker.announcementMessage}
            showClearAll={picker.showClearAll}
            itemIcon={groupIcon}
            tabsLabels={tabLabels}
            selectedTabIndex={activeTabIndex}
            onTabChange={setActiveTabIndex}
            onSearchChange={picker.onSearchChange}
            onAbortSearch={picker.onAbortSearch}
            onSelectionChange={picker.onSelectionChange}
            onSingleSelectItem={handleSingleSelectItem}
            onClearAll={picker.clearAll}
            onApply={() => picker.apply()}
            onCancel={() => picker.cancel()}
            onDismiss={picker.resetDraft}
        />
    );
};

export default TabFilterButton;
